// Common profanity words to filter out (simplified list for demonstration)
export const PROFANITY_LIST = [
  'ass',
  'shit',
  'fuck',
  'bitch',
  'dick',
  'cock',
  'pussy',
  'cunt',
  'bastard',
  'whore',
  'slut',
  'Nazi',
  'hitler',
  'damn',
  'piss',
  'crap',
  'hell',
  'dumb',
];

// Common character substitutions used to bypass filters
export const LEETSPEAK_MAP: Record<string, string[]> = {
  a: ['4', '@', 'Δ', 'α', 'а'],
  b: ['8', '6', 'ß', 'б'],
  c: ['\\(', '\\[', '¢', '<', 'с'],
  e: ['3', '€', 'ε', 'е'],
  f: ['ph', 'ƒ'],
  g: ['6', '9', 'ġ'],
  i: ['1', '!', '\\|', 'ι', 'і'],
  k: ['к'],
  l: ['1', '\\|', '£', 'і'],
  n: ['ո', 'η'],
  o: ['0', 'Ο', 'ο', 'о'],
  p: ['ρ', 'р'],
  s: ['5', '\\$', 'ѕ'],
  t: ['7', '\\+', 'τ', 'т'],
  u: ['v', 'μ', 'υ'],
  v: ['ν'],
  w: ['vv', 'шш'],
  x: ['×', 'х'],
  y: ['ý', 'γ', 'у'],
};

/**
 * Normalizes text by replacing leetspeak and special characters with standard letters
 * @param text Text to normalize
 * @returns Normalized text with leetspeak and special characters converted
 */
export const normalizeLeetspeak = (text: string): string => {
  if (!text) return '';

  let normalized = text.toLowerCase();

  // Replace leetspeak characters with their normal equivalents
  for (const [char, replacements] of Object.entries(LEETSPEAK_MAP)) {
    for (const replacement of replacements) {
      try {
        const regex = new RegExp(replacement, 'g');
        normalized = normalized.replace(regex, char);
      } catch (error) {
        console.error(`Invalid regex pattern: ${replacement}`, error);
        // Continue with other replacements if one fails
      }
    }
  }

  // Also handle repeating characters (like "asssss" or "f***k")
  normalized = normalized.replace(/([a-z])\1+/g, '$1');

  // Handle common patterns for bypassing filters
  normalized = normalized.replace(/\s+/g, ''); // Remove spaces
  normalized = normalized.replace(/[._\-*]/g, ''); // Remove dots, underscores, hyphens, etc.

  return normalized;
};

/**
 * Checks if a string contains profanity
 * @param text Text to check for profanity
 * @returns True if profanity is detected, false otherwise
 */
export const containsProfanity = (text: string): boolean => {
  if (!text) return false;

  // Get normalized version for checking
  const normalized = normalizeLeetspeak(text);

  // Check each profanity word
  for (const word of PROFANITY_LIST) {
    if (normalized.includes(word)) {
      return true;
    }

    // Also check with regex
    const regex = new RegExp(word, 'gi');
    if (regex.test(text)) {
      return true;
    }
  }

  return false;
};

/**
 * Censors profanity in a string, replacing inappropriate words with asterisks
 * @param text Text to censor
 * @returns Censored text with profanity replaced by asterisks
 */
export const censorProfanity = (text: string): string => {
  if (!text) return '';

  // First, get a normalized version for checking
  const normalized = normalizeLeetspeak(text);

  // Make a copy of the original text that we'll censor
  let censored = text;

  // Check each profanity word against the normalized text
  for (const word of PROFANITY_LIST) {
    // Try to find the profanity word in the normalized text
    const index = normalized.indexOf(word);

    if (index >= 0) {
      // If found, censor the corresponding part in the original text
      const startIndex = index;
      const endIndex = index + word.length;

      // Map the normalized indices back to the original text
      // This is an approximation since character counts might differ
      const originalSubstring = text.substring(
        startIndex,
        Math.min(text.length, endIndex + 2)
      );
      censored = censored.replace(
        originalSubstring,
        '*'.repeat(originalSubstring.length)
      );
    }
  }

  // Also do a simpler check with regex for any obvious matches
  for (const word of PROFANITY_LIST) {
    const regex = new RegExp(word, 'gi');
    if (regex.test(censored)) {
      censored = censored.replace(regex, (match) => '*'.repeat(match.length));
    }
  }

  return censored;
};
